import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";

import FilterChip from "../filter-chip/FilterChip";
import UserBubbles from "../../common/UserBubbles/UserBubbles";
import Checkbox from "../../common/checkbox/Checkbox";
import PermissionGate from "../../../permissions/permissionGate";

import { STATUSES, STATUS_ORDER } from "../../../constants/Tasks";
import { dueMeta, isSequential, isInactiveEngagement, userFullName } from "../taskUtils";

const STATUS_OPTIONS = STATUS_ORDER.map(id => ({ id, label: STATUSES[id].name, tone: id }));

/**
 * One row of the task list.
 *
 * A PureComponent so typing in the search box or opening a popover only re-renders the rows whose props changed.
 * `handlers` is a stable object owned by the page: open, toggle, openStatusMenu, closeMenus, setStatus, done, del, formatDate.
 */
class TaskRow extends React.PureComponent {
	stop = event => event.stopPropagation();

	onOpen = () => this.props.handlers.open(this.props.task);
	onToggle = () => this.props.handlers.toggle(this.props.task);
	onOpenStatusMenu = () => this.props.handlers.openStatusMenu(this.props.task);
	onStatusPick = option => this.props.handlers.setStatus(this.props.task, option.id);
	onDone = () => this.props.handlers.done(this.props.task);
	onDelete = () => this.props.handlers.del(this.props.task);

	render() {
		const { task, selected, dimmed, statusMenuOpen, clientGroupName, handlers } = this.props;

		const due = dueMeta(task);
		const status = STATUSES[task.status] || { id: task.status, name: task.status };
		const description = task.description_preview || task.description;

		return (
			<div
				className={clsx("t__row", "tasks-t__row", {
					"tasks-t__row--selected": selected,
					"tasks-t__row--done": task.status === STATUSES.done.id,
					"t__row--dimmed": dimmed
				})}
				onClick={this.onOpen}
				data-taskid={task.id}
				title={dimmed ? "Not the current step of this sequential engagement" : undefined}
			>
				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--check")} onClick={this.stop}>
					<Checkbox name="selectTask" checked={selected} onChange={this.onToggle} isSmall />
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--task")}>
					<div className="tasks-t__title-row">
						{task.read === false && <span className="tasks-t__unread" title="New" />}
						<span className="tasks-t__title ft-task-name-col">{task.name}</span>
					</div>
					{description && <div className="tasks-t__desc ft-task-desc-col">{description}</div>}
					<div className="tasks-t__meta">
						{task.Engagement && (
							<span className="tasks-t__eng">
								<Icon.Folder size={12} />
								{task.Engagement.name}
							</span>
						)}
						{isSequential(task) && task.step > 0 && (
							<span className="tasks-badge tasks-badge--step" title="Sequential engagement">
								Step {task.step}
							</span>
						)}
						{task.sent_back_reason && (
							<span className="tasks-badge tasks-badge--sentback" title={task.sent_back_reason}>
								<Icon.CornerDownLeft size={11} />
								Sent back
							</span>
						)}
						{isInactiveEngagement(task) && <span className="tasks-badge tasks-badge--inactive">Inactive engagement</span>}
					</div>
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--client", "tasks__row-item--client-name")}>
					<div className="tasks-t__client">{task.Client ? task.Client.name : ""}</div>
					{clientGroupName && <div className="tasks-t__client-group">{clientGroupName}</div>}
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--users")}>
					<div className="ft-task-user-col">
						<UserBubbles engagementUsers={task.task_users} />
					</div>
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--status")} onClick={this.stop}>
					<PermissionGate resource="task" action="update">
						<FilterChip
							variant="pill"
							tone={task.status}
							label={status.name}
							labelClassName="ft-task-status-col"
							className="tasks-status-pill"
							title="Change status"
							open={statusMenuOpen}
							onOpen={this.onOpenStatusMenu}
							onClose={handlers.closeMenus}
							single
							minWidth={190}
							options={STATUS_OPTIONS.map(option => ({ ...option, checked: option.id === task.status }))}
							onToggle={this.onStatusPick}
						/>
					</PermissionGate>
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--due")}>
					<div className={clsx("tasks-t__due", `tasks-t__due--${due.tone}`, "ft-task-deadline-col")}>{handlers.formatDate(task.deadline)}</div>
					<div className={clsx("tasks-t__due-meta", `tasks-t__due--${due.tone}`)}>{due.meta}</div>
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--by", "tasks__row-item--assigned-by")}>
					{task.CreatedByUser ? userFullName(task.CreatedByUser) : "-"}
				</div>

				<div className={clsx("t__row__item", "tasks-t__col", "tasks-t__col--actions")} onClick={this.stop}>
					<div className="tasks-t__actions">
						<PermissionGate resource="task" action="update">
							{task.status !== STATUSES.done.id ? (
								<button type="button" className="tasks__icon-btn tasks__icon-btn--ghost ft-task-row-done" title="Mark done" aria-label="Mark done" onClick={this.onDone}>
									<Icon.Check size={15} />
								</button>
							) : (
								<span />
							)}
						</PermissionGate>
						<PermissionGate resource="task" action="delete">
							<button type="button" className="tasks__icon-btn tasks__icon-btn--ghost tasks__icon-btn--danger ft-task-row-delete" title="Delete" aria-label="Delete task" onClick={this.onDelete}>
								<Icon.Trash2 size={14} />
							</button>
						</PermissionGate>
					</div>
				</div>
			</div>
		);
	}
}

export default TaskRow;
